/** Simulation equipment; this catalogue makes no claim about real fleet allocations. */
export const services = [
  'Feuerwehr',
  'Rettungsdienst',
  'Polizei',
  'Hilfsorganisation',
  'THW',
  'Wasserrettung',
  'Bergrettung',
  'Luftrettung',
] as const;
export const vehicleKinds = [
  'KdoW',
  'GW-L',
  'WLF',
  'TSF',
  'HLF',
  'LF',
  'TLF',
  'DLK',
  'RW',
  'ELW',
  'MTF',
  'RTW',
  'NEF',
  'KTW',
  'HvO',
  'GW-San',
  'SEG-Betreuung',
  'FuStW',
  'SEK',
  'MEK',
  'BePo',
  'DHuFü',
  'WaWe',
  'GKW',
  'MzGW',
  'GW-Wasser',
  'RTB',
  'Bergrettung',
  'RTH',
  'ITH',
] as const;
export type ServiceName = (typeof services)[number];
export type KindName = (typeof vehicleKinds)[number];
export const vehicleDefinitions: Record<
  KindName,
  {
    label: string;
    services: readonly ServiceName[];
    category: 'fire' | 'ambulance' | 'police' | 'technical';
    description: string;
  }
> = {
  KdoW: {
    label: 'Kommandowagen',
    services: ['Feuerwehr', 'Rettungsdienst', 'Hilfsorganisation', 'THW', 'Polizei'],
    category: 'fire',
    description: 'Führungs- und Erkundungsfahrzeug',
  },
  'GW-L': {
    label: 'Gerätewagen Logistik / Transport',
    services: ['Feuerwehr', 'Hilfsorganisation', 'THW', 'Rettungsdienst', 'Wasserrettung'],
    category: 'technical',
    description: 'Materialtransport und Logistik; kein Ersatz für Lösch- oder Rettungsfahrzeuge',
  },
  WLF: {
    label: 'Wechselladerfahrzeug',
    services: ['Feuerwehr', 'THW', 'Hilfsorganisation'],
    category: 'technical',
    description: 'Trägerfahrzeug für Abrollbehälter; Beladungsmodule werden separat dokumentiert',
  },
  TSF: {
    label: 'Tragkraftspritzenfahrzeug',
    services: ['Feuerwehr'],
    category: 'fire',
    description: 'Tragkraftspritze und Staffel; ersetzt kein HLF im schweren Hilfeleistungseinsatz',
  },
  HLF: {
    label: 'Hilfeleistungslöschfahrzeug',
    services: ['Feuerwehr'],
    category: 'fire',
    description: 'Löschen und technische Menschenrettung',
  },
  LF: {
    label: 'Löschgruppenfahrzeug',
    services: ['Feuerwehr'],
    category: 'fire',
    description: 'Erfüllt Löschbedarf bei Gebäudebränden',
  },
  TLF: {
    label: 'Tanklöschfahrzeug',
    services: ['Feuerwehr'],
    category: 'fire',
    description: 'Zusätzliche Brandbekämpfung und Wasserversorgung',
  },
  DLK: {
    label: 'Drehleiter',
    services: ['Feuerwehr'],
    category: 'fire',
    description: 'Höhenrettung und Anleiterbereitschaft',
  },
  RW: {
    label: 'Rüstwagen',
    services: ['Feuerwehr'],
    category: 'fire',
    description: 'Erfüllt den technischen Rettungsbedarf beim Verkehrsunfall',
  },
  ELW: {
    label: 'Einsatzleitwagen',
    services: ['Feuerwehr', 'Hilfsorganisation', 'THW'],
    category: 'fire',
    description: 'Führungsunterstützung',
  },
  MTF: {
    label: 'Mannschaftstransportfahrzeug',
    services: ['Feuerwehr', 'Hilfsorganisation', 'THW', 'Wasserrettung', 'Bergrettung'],
    category: 'technical',
    description: 'Personal und zusätzliche Kräfte',
  },
  RTW: {
    label: 'Rettungswagen',
    services: ['Rettungsdienst', 'Hilfsorganisation'],
    category: 'ambulance',
    description: 'Reguläre Notfallversorgung',
  },
  NEF: {
    label: 'Notarzteinsatzfahrzeug',
    services: ['Rettungsdienst', 'Hilfsorganisation'],
    category: 'ambulance',
    description: 'Notärztliche Versorgung',
  },
  KTW: {
    label: 'Krankentransportwagen',
    services: ['Rettungsdienst', 'Hilfsorganisation'],
    category: 'ambulance',
    description: 'Unterstützung; kein Ersatz für einen benötigten RTW',
  },
  HvO: {
    label: 'Helfer vor Ort / First Responder',
    services: ['Hilfsorganisation', 'Rettungsdienst', 'Feuerwehr', 'Wasserrettung', 'Bergrettung'],
    category: 'ambulance',
    description: 'Frühe Erstversorgung; ersetzt weder RTW noch Notarzt',
  },
  'GW-San': {
    label: 'Gerätewagen Sanität / SEG',
    services: ['Hilfsorganisation', 'Rettungsdienst'],
    category: 'ambulance',
    description: 'Sanitätsunterstützung und Behandlungskapazität',
  },
  'SEG-Betreuung': {
    label: 'Schnelleinsatzgruppe Betreuung',
    services: ['Hilfsorganisation'],
    category: 'ambulance',
    description: 'Betreuung von Betroffenen',
  },
  FuStW: {
    label: 'Funkstreifenwagen',
    services: ['Polizei'],
    category: 'police',
    description: 'Absicherung und allgemeine Polizeiaufgaben',
  },
  SEK: {
    label: 'Spezialeinsatzkommando',
    services: ['Polizei'],
    category: 'police',
    description: 'Spezialressource für eine simulierte besondere Polizeilage',
  },
  MEK: {
    label: 'Mobiles Einsatzkommando',
    services: ['Polizei'],
    category: 'police',
    description: 'Spezialressource und Lageunterstützung',
  },
  BePo: {
    label: 'Bereitschaftspolizei',
    services: ['Polizei'],
    category: 'police',
    description: 'Zusätzliche polizeiliche Absicherung',
  },
  DHuFü: {
    label: 'Diensthundeführer',
    services: ['Polizei'],
    category: 'police',
    description: 'Suchunterstützung',
  },
  WaWe: {
    label: 'Wasserwerfer',
    services: ['Polizei'],
    category: 'police',
    description: 'Spezialressource; im MVP nur zusätzliche Kräfte',
  },
  GKW: {
    label: 'THW-Gerätekraftwagen',
    services: ['THW'],
    category: 'technical',
    description: 'Technische Unterstützung',
  },
  MzGW: {
    label: 'THW-Mehrzweckgerätewagen',
    services: ['THW'],
    category: 'technical',
    description: 'Technische und logistische Unterstützung',
  },
  'GW-Wasser': {
    label: 'Gerätewagen Wasserrettung',
    services: ['Wasserrettung', 'Feuerwehr', 'Hilfsorganisation'],
    category: 'technical',
    description: 'Erfüllt Wasserrettungsbedarf',
  },
  RTB: {
    label: 'Wasserrettungseinheit / Rettungsboot',
    services: ['Wasserrettung', 'Feuerwehr'],
    category: 'technical',
    description: 'Wasserrettung mit vereinfachter Anfahrt zum Einsatzort',
  },
  Bergrettung: {
    label: 'Bergrettungseinheit',
    services: ['Bergrettung'],
    category: 'technical',
    description: 'Rettung im unwegsamen Gelände',
  },
  RTH: {
    label: 'Rettungshubschrauber',
    services: ['Luftrettung', 'Rettungsdienst'],
    category: 'ambulance',
    description: 'Notarzt / Luftrettung; vereinfachte Luftlinien-Anflugzeit',
  },
  ITH: {
    label: 'Intensivtransporthubschrauber',
    services: ['Luftrettung'],
    category: 'ambulance',
    description: 'Luftrettungsunterstützung',
  },
};
export const organizationSuggestions = [
  'Kommunale Feuerwehr',
  'Freiwillige Feuerwehr',
  'Berufsfeuerwehr',
  'Werkfeuerwehr',
  'DRK',
  'BRK',
  'ASB',
  'Johanniter',
  'Malteser',
  'DLRG',
  'Wasserwacht',
  'Bergwacht',
  'THW',
  'Landespolizei',
  'Bundespolizei',
  'ADAC Luftrettung',
  'DRF Luftrettung',
  'Privater Rettungsdienst',
  'Rettungsdienstverein / Förderverein',
];
export function canStationHost(service: ServiceName, kind: KindName) {
  return vehicleDefinitions[kind].services.includes(service);
}
export const stationFleet: Record<ServiceName, KindName[]> = {
  Feuerwehr: ['HLF', 'DLK'],
  Rettungsdienst: ['RTW', 'NEF'],
  Polizei: ['FuStW'],
  Hilfsorganisation: ['HvO', 'KTW', 'GW-San', 'SEG-Betreuung'],
  THW: ['GKW', 'MzGW'],
  Wasserrettung: ['GW-Wasser', 'RTB'],
  Bergrettung: ['Bergrettung'],
  Luftrettung: ['RTH'],
};
export const specialtyPackages: Record<
  string,
  { label: string; kinds: KindName[]; description: string }
> = {
  hvo: {
    label: 'HvO / First Responder',
    kinds: ['HvO'],
    description: 'Ehrenamtliche Erstversorgung vor Eintreffen des Rettungsdienstes.',
  },
  sanitaet: {
    label: 'SEG Sanität & Betreuung',
    kinds: ['KTW', 'GW-San', 'SEG-Betreuung'],
    description: 'Simulierte Sanitäts- und Betreuungseinheiten eines Vereins.',
  },
  spezialpolizei: {
    label: 'SEK / MEK / Bereitschaftspolizei',
    kinds: ['SEK', 'MEK', 'BePo', 'DHuFü'],
    description:
      'Fiktive Spezialressourcen. Keine Aussage über reale Standorte, Stärke oder Bereitschaft.',
  },
  fuehrung: {
    label: 'Führung & Personal',
    kinds: ['ELW', 'MTF'],
    description: 'Unterstützung der Einsatzleitung und Personaltransport.',
  },
};
